import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { S3GRole } from '../../generated-types/authRoles';
import useSharedAuthLib from '../../lib/useSharedAuthLib';

/**
 * The properties for the auth guard
 */
interface AuthGuardProps {
  /**
   * The child component within this auth guard
   */
  children: string | JSX.Element | JSX.Element[] | (() => JSX.Element);
  /**
   * Only show the child component if the user has no roles associated with them yet
   */
  onlyNoRoles?: Boolean;
  /**
   * (Optional) The roles that are required by this auth guard
   */
  requiredRoles?: S3GRole[];
  /**
   * (Optional) When un-authentication is detected, redirect to the login page. Default is false.
   */
  unauthToLogin?: Boolean;
}

/**
 * The auth guard component is wrapped around components to prevent access to them, according to the required roles parameter. This guard will
 *  also redirect the user to the login page, if they are unauthenticated for the requested component. It will attempt to redirect the user
 *  back to where they were, if they were originally authenticated for the resource.
 */
const AuthGuard = ({
  requiredRoles,
  children,
  unauthToLogin = false,
  onlyNoRoles = false,
}: AuthGuardProps) => {
  const authLib = useSharedAuthLib();

  /**
   * Determine if the user has valid access for the specified component
   */
  const validAccess = useMemo(() => {
    if (!authLib.Initialized || window.location.pathname == '/login')
      return false;

    // By default, this component guards against not authenticated in users
    if (!authLib.AuthState.Authenticated) return false;

    // Check to see if we should only show this if there are no roles
    if (onlyNoRoles) {
      const roles = authLib.AuthState.UserRoles;
      return roles.length == 0 || roles.every((r) => r == undefined);
    }

    // If we don't need to process any roles, we can end early
    if (!requiredRoles) {
      return true;
    }

    // Check to see if the user is allowed access
    const allowedAccess = authLib.CheckRoles(requiredRoles);

    // Valid access is false by default, but if we get this far then all we need to
    //  do is ensure the user has the correct roles to determine valid access
    return allowedAccess;
  }, [authLib, onlyNoRoles, requiredRoles]);

  return validAccess ? (
    <>{children}</>
  ) : unauthToLogin ? (
    <Navigate to="/login" />
  ) : (
    <></>
  );
};

export default AuthGuard;
